import { ChangeEventHandler, useEffect, useState } from 'react';
import {
  Grid, Paper, Stack, TextField, FormControl,
  RadioGroup, FormControlLabel, Radio, Typography,
} from '@mui/material';
import { v4 as uuid } from 'uuid';
import BlockProps from '../../../models/BlockProps';
import BlockiFrameSafeData, { IFrameServiceType } from '../BlockiFrameSafe/BlockiFrameSafeData';
import CloseButton from '../../Buttons/Close';
import EditableText from '../../Form/EditableText';
import Utils from '../../../utils/Utils';
import { stripScripts, validateIframeSrc } from '../../../utils/htmlSanitization';

function BlockiFrameServices({
  data, isEditing, isTemplate, editHandler,
}: BlockProps) {
  const props = (data.data as BlockiFrameSafeData);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (props.html?.value) {
      const error = validateIframeSrc(props.html.value, props.serviceType ?? IFrameServiceType.GOOGLE_MAP);
      setValidationError(error);
    } else {
      setValidationError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleServiceTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    props.serviceType = event.target.value as IFrameServiceType;
    if (props.html?.value) {
      const error = validateIframeSrc(props.html.value, props.serviceType);
      setValidationError(error);
      if (error) return;
    } else {
      setValidationError(null);
    }
    editHandler(data.id, props);
  };

  const handleHtml: ChangeEventHandler<HTMLInputElement> = (event) => {
    props.html.value = stripScripts(event.target.value);
  };

  const handleBlur: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    const sanitized = stripScripts(event.target.value);
    props.html.value = sanitized;
    if (sanitized) {
      const error = validateIframeSrc(sanitized, props.serviceType ?? IFrameServiceType.GOOGLE_MAP);
      setValidationError(error);
      if (error) return;
    } else {
      setValidationError(null);
    }
    editHandler(data.id, props);
  };

  const sanitizedHtml = props.html?.value ? stripScripts(props.html.value) : '';

  return (
    <Stack
      spacing={0}
      style={{
        overflow: 'hidden',
      }}
    >
      {(isEditing && !isTemplate) && (
        <Stack spacing={0} sx={{ border: '1px dashed lightGray', p: 2, m: 2 }}>
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <RadioGroup
              row
              value={props.serviceType ?? IFrameServiceType.GOOGLE_MAP}
              onChange={handleServiceTypeChange}
            >
              <FormControlLabel
                value={IFrameServiceType.GOOGLE_MAP}
                control={<Radio />}
                label="GoogleMap"
              />
              <FormControlLabel
                value={IFrameServiceType.YOUTUBE}
                control={<Radio />}
                label="Youtube"
              />
              <FormControlLabel
                value={IFrameServiceType.INSTAGRAM}
                control={<Radio />}
                label="Instagram"
              />
            </RadioGroup>
          </FormControl>
          <TextField
            sx={{
              '& textarea': {
                resize: 'vertical',
              },
            }}
            key={uuid()}
            label="iFrame"
            multiline
            rows={4}
            defaultValue={props.html?.value}
            onChange={handleHtml}
            onBlur={handleBlur}
          />
          {validationError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {validationError}
            </Typography>
          )}
        </Stack>
      )}

      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        className="block-content-wrapper"
      >
        <Grid
          container
          direction="column"
          sx={{
            justifyContent: 'center', alignItems: 'center', p: 2,
          }}
        >
          {sanitizedHtml && !validationError && (
            <div style={{ width: '100%' }} dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
          )}
          {(!sanitizedHtml || validationError) && (
            <Paper
              elevation={0}
              sx={{
                width: '100%', height: 450, background: '#F4F4F6', border: '1px dashed lightGray',
              }}
            />
          )}
          {props.text && (
            <Grid item sx={[Utils.hoverBorder(isEditing), { marginTop: 2 }]}>
              {isEditing && (
                <CloseButton updateHandler={() => {
                  props.text = undefined;
                  editHandler(data.id, props);
                }}
                />
              )}
              <EditableText
                text={props.text?.value}
                textColor={props.text?.settings?.textColor}
                isEditable={isEditing && !isTemplate}
                updateHandler={(newText) => {
                  props.text.value = newText;
                  editHandler(data.id, props);
                }}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Stack>
  );
}

export default BlockiFrameServices;
